// Centralized SEO helper for setting per-route meta tags in a SPA.
// All updates are idempotent and run in document.head.

const SITE_URL = "https://allentravelsrd.com";

export interface SeoOptions {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string; // absolute or path (leading slash)
  ogImage?: string;
  ogType?: string; // e.g. "website", "article", "product"
  robots?: string; // e.g. "index,follow" or "noindex,follow"
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
    return el;
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  return el;
}

function setMetaName(name: string, content: string | undefined | null) {
  if (!content) return;
  upsertMeta(`meta[name="${name}"]`, { name, content });
}

function setMetaProp(property: string, content: string | undefined | null) {
  if (!content) return;
  upsertMeta(`meta[property="${property}"]`, { property, content });
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

function setJsonLd(data: SeoOptions["jsonLd"]) {
  // Remove previous route-scoped JSON-LD
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-seo="route"]')
    .forEach((n) => n.remove());

  if (!data) return;
  const items = Array.isArray(data) ? data : [data];
  items.forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "route");
    script.text = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

export function absoluteUrl(pathOrUrl?: string): string {
  if (!pathOrUrl) return SITE_URL + (typeof window !== "undefined" ? window.location.pathname : "/");
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + (pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
}

export function applySeo(opts: SeoOptions | null | undefined) {
  if (!opts) return;
  const {
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType = "website",
    robots = "index,follow",
    jsonLd,
  } = opts;

  if (title) document.title = title;

  setMetaName("description", description);
  setMetaName("keywords", keywords);
  setMetaName("robots", robots);

  const canonicalUrl = absoluteUrl(canonical);
  setCanonical(canonicalUrl);

  setMetaProp("og:title", title);
  setMetaProp("og:description", description);
  setMetaProp("og:type", ogType);
  setMetaProp("og:url", canonicalUrl);
  setMetaProp("og:image", ogImage);
  setMetaProp("og:site_name", "Allen Travels RD");

  setMetaName("twitter:card", ogImage ? "summary_large_image" : "summary");
  setMetaName("twitter:title", title);
  setMetaName("twitter:description", description);
  setMetaName("twitter:image", ogImage);

  setJsonLd(jsonLd);
}
